import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import lodashCompact from 'lodash/fp/compact';
import { getIn, setIn } from 'formik';

import { dataTable } from 'client/helpers/dataTables';

import {
  DATA_TABLE_VIEW_ID_PARTNER_LISTINGS_ALL,
  DATA_TABLE_VIEW_ID_PARTNER_LISTINGS_APP_IN_PROGRESS,
} from 'common/dataTableConstants';

import { hasRolePropertyManager } from 'common/userRoles';

import branch from 'client/core/branch';
import UserActions from 'client/actions/User';

import SrParnterListingsDataTable from 'client/components/SrParnterListingsDataTable/SrParnterListingsDataTable';
import SrUserSettingDropdownWithSearchBar from 'client/components/SrUserSettingDropdownWithSearchBar/SrUserSettingDropdownWithSearchBar';
import SrPartnerSection from 'client/components/SrPartnerSection';
import SrText from 'client/components/SrText';
import SrDataTablePartnerListings, {
  getDataTableSettingsFromProps,
} from 'client/components/SrDataTablePartnerListings';

const VIEW_TYPE_GRID = 'grid';
const VIEW_TYPE_TABLE = 'table';
const VIEW_TYPE_SETTING_ID = 'partnerPortal.activeListings.viewType';
@dataTable(getDataTableSettingsFromProps)
@branch((props) => ({
  partnerListingsViewId: ['partners', 'listingsViewId'],
  currentUser: ['currentUser'],
  partnerUsers: ['partners', 'users', 'listByPartnerId', props.partner.id],
}))
class SrDataTablePartnerListingsSection extends Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    partner: PropTypes.object.isRequired,
    sinceDate: PropTypes.string,
    apartment: PropTypes.object,
    apartmentBuildingId: PropTypes.array,
    partnerListingsViewId: PropTypes.string,
    items: PropTypes.array.isRequired,
    dataTableApiState: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);

    const viewType = getIn(props.currentUser.settings, VIEW_TYPE_SETTING_ID) || VIEW_TYPE_GRID;

    this.state = {
      initialViewType: viewType,
      viewType,
    };
  }

  UNSAFE_componentWillMount() {
    const { partnerListingsViewId } = this.props;
    if (!partnerListingsViewId) {
      this._onFilterClick(DATA_TABLE_VIEW_ID_PARTNER_LISTINGS_ALL);
    }
  }

  _onFilterClick = (viewId) => {
    const { dispatch } = this.props;
    dispatch(({ state }) => {
      state.set(['partners', 'listingsViewId'], viewId);
      state.commit();
    });
  };

  _onViewType(viewType) {
    const { currentUser, dispatch } = this.props;

    this.setState({ viewType });

    const newUserSettings = setIn({ ...currentUser.settings }, VIEW_TYPE_SETTING_ID, viewType);
    dispatch(UserActions.updateSettings, newUserSettings);
  }

  _onLoadListings = (items) => {
    if (items.length >= 9 && this.state.initialViewType === VIEW_TYPE_GRID) {
      this.setState({ viewType: VIEW_TYPE_TABLE });
    }
  };

  render() {
    const {
      partner,
      sinceDate,
      apartment,
      apartmentBuildingId,
      partnerListingsViewId,
      dataTableIsFullyLoaded,
      dataTableApiState,
      items,
      partnerUsers,
      currentUser,
    } = this.props;
    const { viewType } = this.state;

    if (
      (dataTableIsFullyLoaded && !dataTableApiState.inProgress && !items.length) ||
      !partnerListingsViewId
    ) {
      return null;
    }

    const filterOptions = lodashCompact([
      viewType === VIEW_TYPE_GRID && {
        label: 'All',
        value: DATA_TABLE_VIEW_ID_PARTNER_LISTINGS_ALL,
      },
      viewType === VIEW_TYPE_GRID && {
        label: 'Apps In Progress',
        value: DATA_TABLE_VIEW_ID_PARTNER_LISTINGS_APP_IN_PROGRESS,
      },
    ]);

    const propertyManagers = partnerUsers?.filter(({ user }) => hasRolePropertyManager(user.roles));
    const showPropertyManagersFilter =
      partner.experienceConfiguration.listingPropertyManagerEnabled && propertyManagers?.length > 0;

    return (
      <SrPartnerSection
        title="Active Homes"
        headerRow={
          <div
            className="partnerDataTableFilterContainer flex spaceBetween"
            style={{ lineHeight: '40px', flex: 1 }}
          >
            <div className="flex">
              {partner && partner.markets?.length > 1 && (
                <SrUserSettingDropdownWithSearchBar
                  title="Market"
                  notFoundTitle="No markets found"
                  settingField={`partnerPortal.activeListings.${partner.slug}_marketIds`}
                  dropdownOptions={partner.markets.map((option) => ({
                    label: option.displayNameFull,
                    value: option.marketId,
                  }))}
                />
              )}
              {showPropertyManagersFilter && (
                <SrUserSettingDropdownWithSearchBar
                  title="PM"
                  notFoundTitle="No property managers found"
                  settingField={`partnerPortal.activeListings.${partner.slug}_propertyManagerUserIds`}
                  dropdownOptions={propertyManagers.map(({ user }) => ({
                    label: user.name,
                    value: user.id,
                  }))}
                />
              )}
              {filterOptions.map(({ label, value }) => {
                const isActive = value === partnerListingsViewId;
                return (
                  <div key={value} className="partnerDataTableFilterOptionWrapper">
                    <SrText
                      size="large"
                      weight="medium"
                      color="blueGreycliff"
                      cursor={isActive ? null : 'pointer'}
                      decoration={isActive ? 'underline-yellow' : null}
                      font="greycliff"
                      opacity={isActive ? null : 0.5}
                      onClick={isActive ? null : () => this._onFilterClick(value)}
                      className="partnerDataTableFilterOption"
                    >
                      {label}
                    </SrText>
                  </div>
                );
              })}
            </div>
            <div className="flex">
              <div
                className={classnames('sectionIcon grid', { active: viewType === VIEW_TYPE_GRID })}
                onClick={() => this._onViewType(VIEW_TYPE_GRID)}
              />
              <div
                className={classnames('sectionIcon table', {
                  active: viewType === VIEW_TYPE_TABLE,
                })}
                onClick={() => this._onViewType(VIEW_TYPE_TABLE)}
              />
            </div>
          </div>
        }
      >
        {viewType === VIEW_TYPE_GRID && (
          <SrDataTablePartnerListings
            partner={partner}
            sinceDate={sinceDate}
            apartment={apartment}
            apartmentBuildingId={apartmentBuildingId}
            partnerListingsViewId={partnerListingsViewId}
            onLoad={this._onLoadListings}
          />
        )}

        {viewType === VIEW_TYPE_TABLE && (
          <SrParnterListingsDataTable partner={partner} currentUser={currentUser} />
        )}
      </SrPartnerSection>
    );
  }
}

SrDataTablePartnerListingsSection.displayName = 'SrDataTablePartnerListingsSection';

export default SrDataTablePartnerListingsSection;
